import React, { useEffect, useState } from "react";
import "./AddNewHoliday.css";
import { CalendarPicker } from '@mui/x-date-pickers/CalendarPicker';
import { LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs from "dayjs";
import FadeLoader from "react-spinners/FadeLoader";

const DemoItem = ({ label, children }) => (
    <div className="demo-item">
        <p className="demo-label">{label}</p>
        {children}
    </div>
);

const AddNewHoliday = ({
    handleCancelHoliday,
    editPopup,
    setEditPopup,
    holidayData,
    fetchHolidays,
    setIsHolidayPopupOpen,
    setLoader,
    loader,
    setLoading,
    loading,
}) => {
    const [holidayName, setHolidayName] = useState(holidayData?.name || "");
    const [regionType, setRegionType] = useState(holidayData?.region || "");
    const [selectedDate, setSelectedDate] = useState(() => {
        const date = holidayData?.date;
        return date && dayjs(date).isValid() ? dayjs(date) : null
    }
    );
    const [selectedMonth, setSelectedMonth] = useState(holidayData?.month || "");
    const [selectedDay, setSelectedDay] = useState(holidayData?.day || "");
    const [errors, setErrors] = useState({});
    const [everyValue, setEveryValue] = useState(holidayData?.day || "");
    const [inValue, setInValue] = useState(holidayData?.month || "");
    const [holidayType, setHolidayType] = useState(holidayData?.holidayType || 1);
    const [selectedOption, setSelectedOption] = useState('date' || '');
    const hostPath = process.env.REACT_APP_API_BASE_URL;
    const hostHeader = process.env.REACT_APP_CUSTOM_HEADER;

    useEffect(() => {
        switch (holidayType) {
            case 0:
            case 1:
                setSelectedOption('date');
                break;
            case 2:
                setSelectedOption('yearly');
                break;
            default:
                setSelectedOption('custom');
        }
    }, [holidayType]);

    const dayMap = {
        Sunday: 0,
        Monday: 1,
        Tuesday: 2,
        Wednesday: 3,
        Thursday: 4,
        Friday: 5,
        Saturday: 6,
    };

    const reverseDayMap = Object.fromEntries(
        Object.entries(dayMap).map(([day, number]) => [number, day])
    );
    const [ofValue, setOfValue] = useState(() => {
        const weekDayNumber = holidayData?.week;
        return reverseDayMap[weekDayNumber] || "";
    });

    const getDaysInMonth = (month) => {
        const parsedMonth = parseInt(month);

        if (parsedMonth === 2) return 29;
        if ([4, 6, 9, 11].includes(parsedMonth)) return 30;
        return 31;
    };

    const handleChange = (field, value) => {
        if (field === "holidayName") {
            setHolidayName(value);
            setErrors((prev) => ({
                ...prev,
                holidayName: value.trim() ? "" : "Holiday name is required.",
            }));
        } else if (field === "RegionType") {
            setRegionType(value);
            setErrors((prev) => ({
                ...prev,
                RegionType: value ? "" : "Region is required.",
            }));
        }
    };
    const holidayTypeMap = {
        date: 1,
        yearly: 2,
        custom: 3,
    };


    const handleRadioChange = (e) => {
        const selected = e.target.value;

        setSelectedOption(selected);
        setSelectedDate(null);
        setSelectedMonth("");
        setSelectedDay("");
        setErrors((prev) => ({
            ...prev,
            selectedDate: "",
        }));

        setHolidayType(holidayTypeMap[selected]);
    };


    const handleSubmit = async () => {
        setLoading(true);
        const newErrors = {};

        if (!holidayName.trim()) newErrors.holidayName = "Holiday name is required.";
        if (!regionType) newErrors.RegionType = "Region is required.";

        let payload = {
            Name: holidayName,
            Region: regionType,
            HolidayType: holidayType,
            Year: 0,
            Month: 0,
            Day: 0,
            Week: 0,
        };

        if (selectedOption === "date") {
            if (!selectedDate) {
                newErrors.selectedDate = "Date is required.";
            } else {
                payload.Day = selectedDate.date();
                payload.Month = selectedDate.month() + 1;
                payload.Year = selectedDate.year();
            }
        } else if (selectedOption === "yearly") {
            if (!selectedMonth || !selectedDay) {
                newErrors.selectedDate = "Month and day are required.";
            } else {
                payload.Day = Number(selectedDay);
                payload.Month = Number(selectedMonth);
                payload.Year = 0;
            }
        } else if (selectedOption === "custom") {
            if (!everyValue || !ofValue || !inValue) {
                newErrors.selectedDate = "Week, day and month are required.";
            } else {
                payload.Week = dayMap[ofValue];
                payload.Day = Number(everyValue);
                payload.Month = Number(inValue);
                payload.Year = 0;
            }
        }

        if (Object.keys(newErrors).length > 0) {
            setErrors(newErrors);
            setLoading(false);
            return;
        }

        try {
            const url = editPopup
                ? `${hostPath}/api/UpdateHolidayAsync`
                : `${hostPath}/api/AddNewHoliday`;

            const method = editPopup ? "PUT" : "POST";
            const requestPayload = editPopup
                ? {
                    id: holidayData.id,
                    Name: holidayName,
                    ...payload,
                    IsActive: holidayData.isActive,
                    CreatedDate: holidayData.createdDate,
                    LastUpdatedDate: holidayData.LastUpdatedDate,

                }
                : payload;
            const response = await fetch(url, {
                method,
                headers: {
                    "x-functions-key": hostHeader,
                    "Content-Type": "application/json",
                },
                body: JSON.stringify(requestPayload),
            });
            console.log("response", response);


            if (!response.ok) {
                throw new Error(`HTTP error! Status: ${response.status}`);
            }

            setLoader(true);
            fetchHolidays();
            setIsHolidayPopupOpen(false);
        } catch (error) {
            console.error("Error saving holiday:", error);
        } finally {
            setEditPopup(false);
            setLoading(false);
        }
    };


    const isSaveEnabled = () => {
        if (!holidayName.trim() || !regionType || !selectedOption) return false;
        if (selectedOption === "date") {
            return selectedDate !== null;
        }
        if (selectedOption === "yearly") {
            return selectedMonth !== "" && selectedDay !== "";
        }
        if (selectedOption === "custom") {
            return everyValue !== "" && ofValue !== "" && inValue !== "";
        }
        return false;
    };



    return (
        <>
            {loading && (
                <div className="loader-overlay">
                    <FadeLoader />
                </div>
            )}

            <div className="addNewHoliday-container">
                <p className="holiday-title">{editPopup ? "Edit Holiday" : "Add New Holiday"}</p>

                <div className="holiday-input-container">
                    <label htmlFor="HolidayName" className="holiday-label">
                        Holiday Name<span className="mandatorymark">*</span>
                    </label>
                    <input
                        type="text"
                        id="HolidayName"
                        maxLength={100}
                        placeholder="e.g., New Year"
                        className="holiday-input"
                        value={holidayName}
                        onChange={(e) => handleChange("holidayName", e.target.value)}
                    />
                    {errors.holidayName && <p className="error-message">{errors.holidayName}</p>}
                </div>

                <div className="holiday-input-container">
                    <label htmlFor="RegionType" className="holiday-label">
                        Region Name<span className="mandatorymark">*</span>
                    </label>
                    <select
                        id="RegionType"
                        className="holiday-input"
                        value={regionType}
                        onChange={(e) => handleChange("RegionType", e.target.value)}
                    >
                        <option value="">Select Region</option>
                        <option value="United States">United States</option>
                        {/* <option value="France">France</option> */}
                    </select>
                    {errors.RegionType && <p className="error-message">{errors.RegionType}</p>}
                </div>

                <div className="holiday-calender">
                    <div className="holiday-column">

                        <div className="radio-row">
                            <label className="holiday-label">
                                <input
                                    type="radio"
                                    name="holiday-date-option"
                                    value="date"
                                    checked={selectedOption === "date"}
                                    onChange={handleRadioChange}
                                />
                                Date
                            </label>

                            <label className="holiday-label">
                                <input
                                    type="radio"
                                    name="holiday-date-option"
                                    value="yearly"
                                    checked={selectedOption === "yearly"}
                                    onChange={handleRadioChange}
                                />
                                Yearly on Same Date
                            </label>

                            <label className="holiday-label">
                                <input
                                    type="radio"
                                    name="holiday-date-option"
                                    value="custom"
                                    checked={selectedOption === "custom"}
                                    onChange={handleRadioChange}
                                />
                                Custom
                            </label>
                        </div>


                        {selectedOption === "date" && (
                            <div className="calendar-box">
                                <LocalizationProvider dateAdapter={AdapterDayjs}>
                                    <DemoItem>
                                        <CalendarPicker
                                            date={selectedDate}
                                            onChange={(newDate) => setSelectedDate(newDate)}
                                            defaultCalendarMonth={selectedDate && dayjs.isDayjs(selectedDate) ? selectedDate : dayjs()}
                                        />
                                    </DemoItem>
                                </LocalizationProvider>
                            </div>
                        )}

                        {selectedOption === "yearly" && (
                            <div className="yearly-select-row">
                                <select
                                    value={selectedMonth}
                                    onChange={(e) => setSelectedMonth(e.target.value)}
                                    className="select-style"
                                >
                                    <option value="">Select Month</option>
                                    {[
                                        "January", "February", "March", "April", "May", "June",
                                        "July", "August", "September", "October", "November", "December"
                                    ].map((month, index) => (
                                        <option key={index} value={index + 1}>{month}</option>
                                    ))}
                                </select>

                                <select
                                    value={selectedDay}
                                    onChange={(e) => setSelectedDay(e.target.value)}
                                    disabled={!selectedMonth}
                                    className="select-style"
                                >
                                    <option value="">Select Day</option>
                                    {Array.from({ length: getDaysInMonth(selectedMonth) }, (_, i) => i + 1).map((day) => (
                                        <option key={day} value={day}>{day}</option>
                                    ))}
                                </select>
                            </div>
                        )}

                        {selectedOption === "custom" && (
                            <div className="custom-option-row">
                                <div className="custom-group">
                                    <label className="holiday-label">Every</label>
                                    <select
                                        value={everyValue}
                                        onChange={(e) => setEveryValue(e.target.value)}
                                        className="select-style"
                                    >
                                        <option value="">select week</option>
                                        {["1st", "2nd", "3rd", "4th", "5th"].map((label, i) => (
                                            <option key={i} value={i + 1}>{label}</option>
                                        ))}
                                    </select>
                                </div>

                                <div className="custom-group">
                                    <label className="holiday-label">Of</label>
                                    <select
                                        value={ofValue}
                                        onChange={(e) => setOfValue(e.target.value)}
                                        className="select-style"
                                    >
                                        <option value="">Day</option>
                                        {["Sunday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"].map(day => (
                                            <option key={day} value={day}>{day}</option>
                                        ))}
                                    </select>
                                </div>

                                <div className="custom-group">
                                    <label className="holiday-label">In</label>
                                    <select
                                        value={inValue}
                                        onChange={(e) => setInValue(e.target.value)}
                                        className="select-style"
                                    >
                                        <option value="">Month</option>
                                        {[
                                            "January", "February", "March", "April", "May", "June",
                                            "July", "August", "September", "October", "November", "December"
                                        ].map((m, idx) => (
                                            <option key={m} value={idx + 1}>{m}</option>
                                        ))}
                                    </select>
                                </div>
                            </div>
                        )}

                        {errors.selectedDate && <p className="error-message">{errors.selectedDate}</p>}
                        {selectedOption === "date" && selectedDate && (
                            <span className="selected-date-badge">
                                {dayjs(selectedDate).format("MMMM DD, YYYY")}
                            </span>
                        )}
                        {!selectedOption && selectedMonth && selectedDay && (
                            <span className="selected-date-badge">
                                {dayjs(`${selectedMonth}-${selectedDay}`, "M-D").format("MMMM DD")}
                            </span>
                        )}


                    </div>
                </div>

                <div className="holiday-buttons">
                    <button className="cancel-btn" onClick={handleCancelHoliday}>
                        Cancel
                    </button>
                    <button
                        className={isSaveEnabled() ? "add-holiday-btn" : "cancel-holiday-btn"}
                        onClick={handleSubmit} disabled={!isSaveEnabled()}
                        style={{ cursor: isSaveEnabled() ? "pointer" : "not-allowed" }}
                    >
                        {editPopup ? "Update" : "Save"}
                    </button>
                </div>
            </div>
        </>
    );
};

export default AddNewHoliday;
